import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Stack,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';

import { useCreateLessonMutation, useUpdateLessonMutation } from '../lessonsApi';

import { Lesson } from '@/shared/types/lesson';

interface LessonDialogProps {
  open: boolean;
  onClose: () => void;
  unitId: string;
  currentUserId: string;
  lesson?: Lesson;
  mode: 'create' | 'edit';
}

const LessonDialog: React.FC<LessonDialogProps> = ({
  open,
  onClose,
  unitId,
  currentUserId,
  lesson,
  mode,
}) => {
  const [title, setTitle] = useState(lesson?.title || '');
  const [description, setDescription] = useState(lesson?.description || '');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const navigate = useNavigate();
  const { courseId } = useParams<{ courseId: string }>();
  const [createLesson, { isLoading: creating }] = useCreateLessonMutation();
  const [updateLesson, { isLoading: updating }] = useUpdateLessonMutation();

  const isLoading = creating || updating;

  // Reset form when dialog opens with new data
  React.useEffect(() => {
    if (open) {
      setTitle(lesson?.title || '');
      setDescription(lesson?.description || '');
      setErrorMsg(null);
    }
  }, [open, lesson]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!title.trim()) {
      setErrorMsg('Lesson title is required.');
      return;
    }

    setErrorMsg(null);

    try {
      if (mode === 'create') {
        const newLesson = await createLesson({
          title: title.trim(),
          description: description.trim(),
          owner_id: currentUserId,
          unit_id: unitId,
        }).unwrap();
        onClose();
        navigate(`/curriculum/courses/${courseId}/units/${unitId}/lessons/${newLesson.id}`);
        return;
      } else if (lesson) {
        await updateLesson({
          id: lesson.id,
          title: title.trim(),
          description: description.trim(),
        }).unwrap();
      }

      onClose();
    } catch (error) {
      console.error('Lesson operation error:', error);
      setErrorMsg(`Failed to ${mode} lesson. Please try again.`);
    }
  };

  const handleClose = () => {
    setTitle(lesson?.title || '');
    setDescription(lesson?.description || '');
    setErrorMsg(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>{mode === 'create' ? 'Create New Lesson' : 'Edit Lesson'}</DialogTitle>

        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              label="Lesson Title*"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              fullWidth
              autoFocus
              disabled={isLoading}
            />

            <TextField
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth
              multiline
              rows={3}
              disabled={isLoading}
            />

            {errorMsg && <Alert severity="error">{errorMsg}</Alert>}
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={isLoading || !title.trim()}>
            {isLoading ? (
              <CircularProgress size={20} />
            ) : mode === 'create' ? (
              'Create Lesson'
            ) : (
              'Save Changes'
            )}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default LessonDialog;
